import * as React from "react"
import {
  alpha,
  Box,
  Card,
  CardActionArea,
  Typography,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import CameraAltIcon from "@mui/icons-material/CameraAlt"
import TouchAppIcon from "@mui/icons-material/TouchApp"
import TerrainIcon from "@mui/icons-material/Terrain"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"

// Layout's sticky app bar plus its content padding, by breakpoint (matches
// SetProject). On phones the tiles stretch to fill the rest of the screen so
// the homepage reads as an app launcher rather than a scrolling web page.
const CHROME_HEIGHT = { xs: 56 + 16 * 2, md: 64 + 24 * 2 }

const features = [
  {
    title: "Climb",
    blurb: "Pick your gym and see what's on its board right now.",
    path: "/gyms",
    icon: TerrainIcon,
    accent: "#ffb74d",
  },
  {
    title: "Set",
    blurb: "Sketch a problem by touch and light it up on the wall.",
    path: "/set",
    icon: TouchAppIcon,
    accent: "#4dd0e1",
  },
  {
    title: "Identify",
    blurb: "Snap a photo of a kilter board and match it to the climb.",
    path: "/identify",
    icon: CameraAltIcon,
    accent: "#ff3366",
  },
]

export default function Home() {
  const navigate = useNavigate()

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: { xs: `calc(100vh - ${CHROME_HEIGHT.xs}px)`, md: "unset" },
        maxWidth: 960,
        mx: "auto",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gridAutoRows: { xs: "1fr", md: "unset" },
          pb: { xs: 1, md: 0 },
        }}
      >
        {features.map((feature) => {
          const Icon = feature.icon
          return (
            <Card
              key={feature.path}
              sx={{
                display: "flex",
                borderRadius: 4,
                minHeight: { md: 240 },
                transition: "transform 0.2s ease, border-color 0.2s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  borderColor: alpha(feature.accent, 0.5),
                },
              }}
            >
              <CardActionArea
                onClick={() => navigate(feature.path)}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "space-between",
                  gap: 2,
                  background: `radial-gradient(circle at 100% 0%, ${alpha(
                    feature.accent,
                    0.12
                  )}, transparent 55%)`,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(feature.accent, 0.15),
                  }}
                >
                  <Icon sx={{ fontSize: 30, color: feature.accent }} />
                </Box>
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="h3">{feature.title}</Typography>
                    <ArrowForwardIcon sx={{ color: feature.accent }} />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {feature.blurb}
                  </Typography>
                </Box>
              </CardActionArea>
            </Card>
          )
        })}
      </Box>
    </Box>
  )
}
